import { get, del, post, getRaw, putBlob } from '../api.js';
import { hasKeys, getMasterKey, wrapMasterKeyWithPrf, changePassword, checkHIBP, generateBip39Mnemonic, deriveRecoveryKeys, wrapMasterKeyForRecovery, wrapKeyForRecovery, getMasterKeyBytes, rotateMasterKey, applyRotatedKeys, createApiKey, grantItemToApiKey, revokeApiKeyGrant, getRawItemKey, decryptItem, decryptItemRaw, base64urlEncode, getEncKey, getUserId, reEncryptBlobV1, rewrapEnvelopeFileKeys, wipe } from '../crypto.js';
import { isWebAuthnAvailable, startRegistration, activatePrf, finishActivatePrf } from '../webauthn.js';
import { setTopbar, lucideIcons, esc } from '../app.js';
import { confirmDialog, promptDialog } from '../dialog.js';
import { setEmail } from '../auth-store.js';

/**
 * Show a dialog to choose between scoped and full-access API key.
 * Returns true (scoped), false (full-access), or null (cancelled).
 */
function chooseApiKeyType() {
  return new Promise(resolve => {
    const overlay = document.createElement('div');
    overlay.className = 'dialog-overlay';
    overlay.innerHTML = `
      <div class="dialog-box">
        <p class="dialog-message" style="font-weight:600;margin-bottom:12px">API Key Type</p>
        <label style="display:flex;align-items:flex-start;gap:8px;padding:8px;border:1px solid var(--border);border-radius:4px;cursor:pointer;margin-bottom:8px">
          <input type="radio" name="aktype" value="scoped" checked style="margin-top:3px">
          <div>
            <div style="font-weight:500">Scoped</div>
            <div style="font-size:12px;color:var(--text-secondary)">Access only items you explicitly grant. Best for CI/CD.</div>
          </div>
        </label>
        <label style="display:flex;align-items:flex-start;gap:8px;padding:8px;border:1px solid var(--border);border-radius:4px;cursor:pointer;margin-bottom:12px">
          <input type="radio" name="aktype" value="full" style="margin-top:3px">
          <div>
            <div style="font-weight:500">Full Access</div>
            <div style="font-size:12px;color:var(--text-secondary)">Can decrypt all items. Requires your password.</div>
          </div>
        </label>
        <div class="dialog-actions">
          <button class="btn btn-secondary btn-sm dialog-cancel">Cancel</button>
          <button class="btn btn-primary btn-sm dialog-confirm">Continue</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    requestAnimationFrame(() => overlay.classList.add('visible'));

    const close = (result) => {
      overlay.classList.remove('visible');
      overlay.addEventListener('transitionend', () => overlay.remove(), { once: true });
      setTimeout(() => overlay.remove(), 300);
      resolve(result);
    };

    overlay.querySelector('.dialog-cancel').onclick = () => close(null);
    overlay.querySelector('.dialog-confirm').onclick = () => {
      const val = overlay.querySelector('input[name="aktype"]:checked').value;
      close(val === 'scoped');
    };
    overlay.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close(null);
    });
  });
}

/**
 * Fetch all items and decrypt their labels (for CLI-created secrets).
 * Returns [{id, label, wrappedKey, nonce}, ...] for items that have a "secret" label.
 */
async function fetchDecryptedItemLabels() {
  const items = await get('/items');
  const results = [];
  for (const item of items) {
    try {
      const blob = item.encrypted_blob;
      if (!blob) continue;
      const wrappedKey = new Uint8Array(item.wrapped_key);
      const nonce = new Uint8Array(item.nonce);
      const plainBytes = decryptItemRaw(
        blob,
        wrappedKey,
        nonce,
        item.wrapped_key_format_version,
        item.encrypted_blob_format_version,
      );
      const text = new TextDecoder().decode(plainBytes);
      const parsed = JSON.parse(text);
      if (parsed.type === 'secret' && parsed.label) {
        results.push({
          id: item.id,
          label: parsed.label,
          wrappedKey: item.wrapped_key,
          nonce: item.nonce,
        });
      }
    } catch (_) {
      // Skip items we can't decrypt or parse
    }
  }
  results.sort((a, b) => a.label.localeCompare(b.label));
  return results;
}

/**
 * Render an item picker with checkboxes.
 * Returns HTML string and the list of items for reference.
 */
function renderItemPicker(items, selectedIds = new Set()) {
  if (items.length === 0) {
    return '<div style="color:var(--text-secondary);font-size:13px;padding:8px 0">No secrets found. Use <code>vault-cli put</code> to create secrets first.</div>';
  }
  let html = '<div style="max-height:300px;overflow-y:auto;border:1px solid var(--border);border-radius:4px;padding:8px">';
  for (const item of items) {
    const checked = selectedIds.has(item.id) ? 'checked' : '';
    html += `<label style="display:flex;align-items:center;gap:8px;padding:4px 0;cursor:pointer">
      <input type="checkbox" class="item-picker-cb" value="${item.id}" ${checked}>
      <span style="font-family:monospace;font-size:13px">${esc(item.label)}</span>
    </label>`;
  }
  html += '</div>';
  return html;
}

export async function renderSettings(container) {
  setTopbar('Settings', '');
  container.innerHTML = '<p class="loading">Loading...</p>';

  const msg = { el: null };

  try {
    const [passkeys, me, apiKeys] = await Promise.all([
      isWebAuthnAvailable() ? get('/passkeys') : Promise.resolve([]),
      get('/auth/me'),
      hasKeys() ? get('/api-keys').catch(() => []) : Promise.resolve([]),
    ]);

    // Locked state: vault not unlocked, show reduced settings
    if (!hasKeys()) {
      let html = '<div class="section-title">Account</div>';
      html += `
        <div class="card">
          <div class="detail-label">Email</div><div class="detail-value">${esc(me.email)}</div>
          <div class="detail-label">Account Created</div>
          <div class="detail-value">${new Date(me.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
        </div>`;

      html += `
        <div class="msg msg-warning" style="margin-top:24px">
          Your vault is locked. Unlock it to access all settings, or use your recovery words to reset your passphrase.
        </div>`;

      html += `
        <div class="section-title" style="margin-top:24px">Recovery Options</div>
        <div class="card" style="display:flex;flex-direction:column;gap:12px">
          <p style="color:var(--text-secondary);font-size:13px;margin:0">
            If you've lost your passphrase, you can use your 12-word recovery mnemonic to set a new one.
          </p>
          <div style="display:flex;gap:8px">
            <a href="#/unlock" class="btn btn-primary btn-sm" style="text-decoration:none;text-align:center">Unlock Vault</a>
            <a href="#/recover" class="btn btn-secondary btn-sm" style="text-decoration:none;text-align:center">Recover with Mnemonic</a>
          </div>
        </div>`;

      html += `
        <div class="section-title" style="margin-top:32px">Close Account</div>
        <div class="card" style="border-color:var(--danger)">
          <p style="color:var(--text-secondary);font-size:13px;margin-bottom:12px">
            Permanently delete your account and all data. This cannot be undone.
          </p>
          <a href="#/close-account" class="btn btn-danger btn-sm" style="display:inline-block;text-decoration:none;text-align:center">Close Account</a>
        </div>`;

      container.innerHTML = html;
      lucideIcons();
      if (me.email) setEmail(me.email);
      return;
    }

    let html = '<div class="section-title">Passkeys</div>';

    if (!isWebAuthnAvailable()) {
      html += '<div class="msg msg-error" style="margin-bottom:16px">WebAuthn is not supported in this browser.</div>';
    }

    if (passkeys.length) {
      passkeys.forEach(pk => {
        html += `
          <div class="passkey-card">
            <div class="pk-icon"><i data-lucide="fingerprint"></i></div>
            <div class="pk-info">
              <div class="pk-name">${esc(pk.name || '(unnamed)')}</div>
              <div class="pk-meta">
                <span class="badge ${pk.has_prf ? 'badge-prf' : 'badge-auth'}">${pk.has_prf ? 'Passwordless' : 'Auth Only'}</span>
                <span>Added ${new Date(pk.created_at).toLocaleDateString()}</span>
              </div>
            </div>
            <button class="btn btn-danger btn-sm delete-pk-btn" data-id="${pk.id}"><i data-lucide="trash-2" style="width:14px;height:14px"></i></button>
          </div>`;
      });
    } else if (isWebAuthnAvailable()) {
      html += '<p style="color:var(--text-secondary);margin-bottom:12px">No passkeys enrolled yet.</p>';
    }

    if (isWebAuthnAvailable() && hasKeys()) {
      html += `<button class="btn btn-secondary btn-sm" style="margin-top:8px;margin-bottom:32px" id="add-passkey-btn"><i data-lucide="plus" style="width:14px;height:14px"></i> Add Passkey</button>`;
    } else {
      html += '<div style="margin-bottom:32px"></div>';
    }

    html += '<div id="passkey-msg"></div>';

    // Account section
    html += '<div class="section-title">Account</div>';
    html += `
      <div class="card">
        <div class="detail-label">Email</div><div class="detail-value">${esc(me.email)}</div>
        <div class="detail-label">Public Key Fingerprint</div>
        <div class="detail-value mono" style="font-size:12px;color:var(--text-secondary)" id="key-fingerprint">Computing...</div>
        <div class="detail-label">Account Created</div>
        <div class="detail-value">${new Date(me.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
      </div>`;

    // API Keys section
    if (hasKeys()) {
      html += '<div class="section-title" style="margin-top:32px">API Keys</div>';
      html += '<div id="apikey-msg"></div>';

      if (apiKeys.length) {
        apiKeys.forEach(k => {
          const scopeBadge = k.is_scoped
            ? '<span class="badge badge-prf">Scoped</span>'
            : '<span class="badge badge-auth">Full Access</span>';
          const roBadge = k.scopes?.read_only ? ' <span class="badge badge-auth">Read Only</span>' : '';
          const lastUsed = k.last_used_at ? k.last_used_at.slice(0, 10) : 'never';
          const expiry = k.expires_at ? ` · Expires: ${k.expires_at.slice(0, 10)}` : '';
          html += `
            <div class="card" style="margin-bottom:12px">
              <div style="display:flex;justify-content:space-between;align-items:center">
                <div>
                  <strong>${esc(k.name)}</strong> ${scopeBadge}${roBadge}
                </div>
                <div style="display:flex;gap:8px">
                  ${k.is_scoped ? `<button class="btn btn-secondary btn-sm manage-items-btn" data-id="${k.id}" data-pubkey="${k.public_key ? JSON.stringify(k.public_key) : ''}">Items (${k.grant_count || 0})</button>` : ''}
                  <button class="btn btn-danger btn-sm revoke-key-btn" data-id="${k.id}"><i data-lucide="trash-2" style="width:14px;height:14px"></i></button>
                </div>
              </div>
              <div style="font-size:12px;color:var(--text-secondary);margin-top:8px">
                ${esc(k.key_prefix)} · Last used: ${lastUsed}${expiry}
              </div>
            </div>`;
        });
      } else {
        html += '<p style="color:var(--text-secondary);margin-bottom:12px">No API keys created yet.</p>';
      }

      html += `<button class="btn btn-secondary btn-sm" style="margin-bottom:32px" id="create-apikey-btn"><i data-lucide="plus" style="width:14px;height:14px"></i> Create API Key</button>`;
    }

    if (me.encrypted_master_key && hasKeys()) {
      html += `
        <div class="section-title" style="margin-top:32px">Change Passphrase</div>
        <div id="chpw-msg"></div>
        <form id="change-pw-form" class="card" style="display:flex;flex-direction:column;gap:12px">
          <div class="field"><label>Current Password</label><input type="password" name="current" required></div>
          <div class="field"><label>New Passphrase</label><input type="password" name="newpw" required minlength="16" placeholder="e.g. correct horse battery staple"></div>
          <p style="font-size:12px;color:var(--text-secondary);margin:-8px 0 4px">Use 4+ random words. Minimum 16 characters.</p>
          <div class="field"><label>Confirm New Passphrase</label><input type="password" name="confirm" required></div>
          <button type="submit" class="btn btn-primary" style="align-self:flex-start">Change Passphrase</button>
        </form>`;

      // Recovery section
      const hasRecovery = me.has_recovery;
      html += `
        <div class="section-title" style="margin-top:32px">Recovery Words</div>
        <div class="card">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:12px">
            <span class="badge ${hasRecovery ? 'badge-prf' : 'badge-auth'}">${hasRecovery ? 'Configured' : 'Not set up'}</span>
            <span style="color:var(--text-secondary);font-size:13px">${hasRecovery ? 'You can recover your vault with your 12-word mnemonic.' : 'Set up recovery words to protect against passphrase loss.'}</span>
          </div>
          <div id="recovery-msg"></div>
          <button class="btn btn-secondary btn-sm" id="regen-recovery-btn">${hasRecovery ? 'Regenerate Recovery Words' : 'Set Up Recovery Words'}</button>
          <div id="recovery-flow" style="display:none;margin-top:16px"></div>
        </div>`;

      // Key rotation section
      html += `
        <div class="section-title" style="margin-top:32px">Rotate Master Key</div>
        <div class="card">
          <p style="color:var(--text-secondary);font-size:13px;margin-bottom:12px">
            Generate a new master encryption key. All item keys will be re-wrapped. Use this if you suspect your master key may have been compromised.
          </p>
          <div id="rotate-msg"></div>
          <div style="display:flex;flex-direction:column;gap:12px;margin-bottom:12px">
            <div class="field"><label>Current Passphrase</label><input type="password" id="rotate-password" required></div>
            ${me.has_recovery ? '<div class="field"><label>Recovery Words (12 words, space-separated)</label><textarea id="rotate-recovery" rows="2" style="width:100%;font-family:monospace;font-size:13px" placeholder="word1 word2 word3 ..."></textarea></div>' : ''}
          </div>
          <button class="btn btn-secondary btn-sm" id="rotate-key-btn">Rotate Master Key</button>
        </div>`;
    }

    // Close Account section (always visible)
    html += `
      <div class="section-title" style="margin-top:32px">Close Account</div>
      <div class="card" style="border-color:var(--danger)">
        <p style="color:var(--text-secondary);font-size:13px;margin-bottom:12px">
          Permanently delete your account and all data. This cannot be undone.
        </p>
        <a href="#/close-account" class="btn btn-danger btn-sm" style="display:inline-block;text-decoration:none;text-align:center">Close Account</a>
      </div>`;

    container.innerHTML = html;
    lucideIcons();
    msg.el = document.getElementById('passkey-msg');

    // Compute fingerprint asynchronously
    const fpEl = document.getElementById('key-fingerprint');
    if (fpEl) formatKeyFingerprint(me.public_key).then(fp => { fpEl.textContent = fp; });

    // Store email for sidebar
    if (me.email) setEmail(me.email);

    // --- API Key event handlers ---
    const createApikeyBtn = document.getElementById('create-apikey-btn');
    if (createApikeyBtn) {
      createApikeyBtn.onclick = async () => {
        const akMsg = document.getElementById('apikey-msg');
        const name = await promptDialog('API Key Name', 'Enter a name for the new API key', 'e.g. CI production');
        if (!name) return;

        const scoped = await chooseApiKeyType()
        if (scoped === null) return; // cancelled
        let selectedItemIds = [];

        // Step 2: item picker for scoped keys
        if (scoped) {
          akMsg.innerHTML = '<div class="msg">Loading items...</div>';
          try {
            const allItems = await fetchDecryptedItemLabels();
            const pickerHtml = renderItemPicker(allItems);
            akMsg.innerHTML = `
              <div class="card" style="margin-bottom:16px">
                <div style="margin-bottom:8px"><strong>Select items to grant to this key:</strong></div>
                ${pickerHtml}
                <div style="margin-top:12px;display:flex;gap:8px">
                  <button class="btn btn-primary btn-sm" id="apikey-picker-confirm">Create Key</button>
                  <button class="btn btn-secondary btn-sm" id="apikey-picker-cancel">Cancel</button>
                </div>
              </div>`;

            // Wait for confirm or cancel
            const confirmed = await new Promise(resolve => {
              document.getElementById('apikey-picker-confirm').onclick = () => resolve(true);
              document.getElementById('apikey-picker-cancel').onclick = () => resolve(false);
            });

            if (!confirmed) {
              akMsg.innerHTML = '';
              return;
            }

            selectedItemIds = Array.from(akMsg.querySelectorAll('.item-picker-cb:checked')).map(cb => cb.value);
          } catch (e) {
            akMsg.innerHTML = `<div class="msg msg-error">${esc(e.message || String(e))}</div>`;
            return;
          }
        }

        try {
          akMsg.innerHTML = '<div class="msg">Creating API key...</div>';
          const { result, displayKey } = await createApiKey(name, scoped, selectedItemIds, false, null);

          akMsg.innerHTML = `
            <div class="card" style="border-color:var(--accent);margin-bottom:16px">
              <div style="margin-bottom:8px"><strong>API key created!</strong> ${scoped ? '(scoped — ' + selectedItemIds.length + ' item(s) granted)' : '(full access)'}</div>
              <div style="font-size:12px;color:var(--text-secondary);margin-bottom:8px">This key is shown once. Store it securely.</div>
              <div style="display:flex;gap:8px;align-items:center">
                <code style="font-size:12px;word-break:break-all;flex:1;background:var(--bg-secondary);padding:8px;border-radius:4px">${esc(displayKey)}</code>
                <button class="btn btn-secondary btn-sm" id="copy-apikey-btn">Copy</button>
              </div>
              <div style="margin-top:12px"><button class="btn btn-secondary btn-sm" id="dismiss-apikey-btn">Done</button></div>
            </div>`;

          document.getElementById('copy-apikey-btn').onclick = () => {
            navigator.clipboard.writeText(displayKey).then(() => {
              document.getElementById('copy-apikey-btn').textContent = 'Copied!';
            }).catch(() => {});
          };

          document.getElementById('dismiss-apikey-btn').onclick = () => renderSettings(container);
        } catch (e) {
          akMsg.innerHTML = `<div class="msg msg-error">${esc(e.message || String(e))}</div>`;
        }
      };
    }

    // Revoke API key buttons
    container.querySelectorAll('.revoke-key-btn').forEach(btn => {
      btn.onclick = async () => {
        const id = btn.dataset.id;
        const ok = await confirmDialog('This will permanently revoke this API key and all its item grants. Continue?', { danger: true });
        if (!ok) return;
        try {
          await del(`/api-keys/${id}`);
          renderSettings(container);
        } catch (e) {
          const akMsg = document.getElementById('apikey-msg');
          if (akMsg) akMsg.innerHTML = `<div class="msg msg-error">${esc(e.message || String(e))}</div>`;
        }
      };
    });

    // Manage items buttons (scoped keys)
    container.querySelectorAll('.manage-items-btn').forEach(btn => {
      btn.onclick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const keyId = btn.dataset.id;
        const pubkeyJson = btn.dataset.pubkey;
        const akMsg = document.getElementById('apikey-msg');
        try {
          akMsg.innerHTML = '<div class="msg">Loading grants and items...</div>';
          const [grants, allItems] = await Promise.all([
            get(`/api-keys/${keyId}/grants`),
            fetchDecryptedItemLabels(),
          ]);

          // Build a map of item_id → label for display
          const labelMap = new Map(allItems.map(i => [i.id, i.label]));
          const grantedIds = new Set(grants.map(g => g.item_id));

          let html = '<div class="card" style="margin-bottom:16px">';
          html += '<strong>Items granted to this key:</strong><div style="margin-top:8px">';
          if (grants.length === 0) {
            html += '<div style="color:var(--text-secondary);font-size:13px;padding:4px 0">No items granted yet.</div>';
          } else {
            for (const g of grants) {
              const label = labelMap.get(g.item_id) || g.item_id;
              html += `<div style="display:flex;justify-content:space-between;align-items:center;padding:6px 0;border-bottom:1px solid var(--border)">
                <span style="font-size:13px;font-family:monospace">${esc(label)}</span>
                <button class="btn btn-danger btn-sm ungrant-btn" data-key-id="${keyId}" data-grant-id="${g.id}" style="padding:2px 8px;font-size:11px">Remove</button>
              </div>`;
            }
          }
          html += '</div>';

          // Add Item section: show ungrated items as checkboxes
          const ungrantedItems = allItems.filter(i => !grantedIds.has(i.id));
          if (ungrantedItems.length > 0) {
            html += '<div style="margin-top:12px"><strong>Add items:</strong></div>';
            html += '<div style="margin-top:4px">' + renderItemPicker(ungrantedItems) + '</div>';
            html += '<button class="btn btn-primary btn-sm" id="add-grants-btn" style="margin-top:8px">Grant Selected</button>';
          }

          html += '<button class="btn btn-secondary btn-sm" id="close-manage-btn" style="margin-top:8px;margin-left:8px">Close</button>';
          html += '</div>';
          akMsg.innerHTML = html;

          // Close button
          document.getElementById('close-manage-btn').onclick = () => { akMsg.innerHTML = ''; };

          // Ungrant buttons
          akMsg.querySelectorAll('.ungrant-btn').forEach(ub => {
            ub.onclick = async () => {
              try {
                ub.disabled = true;
                ub.textContent = '...';
                await revokeApiKeyGrant(ub.dataset.keyId, ub.dataset.grantId);
                btn.click(); // refresh the panel
              } catch (e) {
                akMsg.innerHTML = `<div class="msg msg-error">${esc(e.message || String(e))}</div>`;
              }
            };
          });

          // Add grants button
          const addBtn = document.getElementById('add-grants-btn');
          if (addBtn) {
            addBtn.onclick = async () => {
              const selected = Array.from(akMsg.querySelectorAll('.item-picker-cb:checked')).map(cb => cb.value);
              if (selected.length === 0) return;

              addBtn.disabled = true;
              addBtn.textContent = `Granting ${selected.length} item(s)...`;

              try {
                let pubkey;
                if (pubkeyJson) {
                  pubkey = new Uint8Array(JSON.parse(pubkeyJson));
                } else {
                  // Fallback: fetch from API
                  const keys = await get('/api-keys');
                  const key = keys.find(k => k.id === keyId);
                  if (!key || !key.public_key) throw new Error('API key public key not found');
                  pubkey = new Uint8Array(key.public_key);
                }

                const encKey = getEncKey();
                try {
                  for (const itemId of selected) {
                    await grantItemToApiKey(keyId, pubkey, itemId, encKey);
                  }
                } finally {
                  wipe(encKey);
                }

                btn.click(); // refresh the panel
              } catch (e) {
                akMsg.innerHTML = `<div class="msg msg-error">${esc(e.message || String(e))}</div>`;
              }
            };
          }
        } catch (e) {
          console.error('Manage items error:', e);
          if (akMsg) akMsg.innerHTML = `<div class="msg msg-error">${esc(e.message || String(e))}</div>`;
        }
      };
    });

    // Change password form handler
    const chpwForm = document.getElementById('change-pw-form');
    if (chpwForm) {
      chpwForm.onsubmit = async (e) => {
        e.preventDefault();
        const chpwMsg = document.getElementById('chpw-msg');
        const btn = chpwForm.querySelector('button');
        const current = chpwForm.current.value;
        const newpw = chpwForm.newpw.value;
        const confirmPw = chpwForm.confirm.value;
        chpwMsg.innerHTML = '';

        if (newpw.length < 16) {
          chpwMsg.innerHTML = '<div class="msg msg-error">Passphrase must be at least 16 characters.</div>';
          return;
        }
        if (newpw !== confirmPw) {
          chpwMsg.innerHTML = '<div class="msg msg-error">New passphrases do not match</div>';
          return;
        }

        btn.disabled = true;
        btn.textContent = 'Checking...';

        const hibp = await checkHIBP(newpw);
        if (hibp.breached) {
          chpwMsg.innerHTML = `<div class="msg msg-error">This password appeared in ${hibp.count.toLocaleString()} data breaches. Choose a different one.</div>`;
          btn.disabled = false;
          btn.textContent = 'Change Passphrase';
          return;
        }

        btn.textContent = 'Changing...';
        try {
          const result = changePassword(current, me.client_salt, newpw);
          await post('/auth/set-password', {
            current_auth_key: result.current_auth_key,
            auth_key: result.auth_key,
            client_salt: result.client_salt,
            encrypted_master_key: result.encrypted_master_key,
            public_key: result.public_key,
            encrypted_private_key: result.encrypted_private_key,
          });
          chpwMsg.innerHTML = '<div class="msg msg-success">Passphrase changed successfully.</div>';
          chpwForm.reset();
        } catch (err) {
          chpwMsg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
        }
        btn.disabled = false;
        btn.textContent = 'Change Passphrase';
      };
    }

    // Recovery flow handler
    const regenBtn = document.getElementById('regen-recovery-btn');
    if (regenBtn) {
      regenBtn.onclick = async () => {
        const recoveryMsg = document.getElementById('recovery-msg');
        const flowEl = document.getElementById('recovery-flow');
        regenBtn.disabled = true;
        recoveryMsg.innerHTML = '';

        try {
          const mnemonic = await generateBip39Mnemonic();
          const words = mnemonic.split(' ');
          const grid = words.map((w, i) => `<div class="recovery-word"><span class="recovery-num">${i + 1}.</span> ${esc(w)}</div>`).join('');

          const positions = [];
          while (positions.length < 3) {
            const p = Math.floor(Math.random() * 12);
            if (!positions.includes(p)) positions.push(p);
          }
          positions.sort((a, b) => a - b);

          flowEl.style.display = '';
          flowEl.innerHTML = `
            <p style="font-weight:600;margin-bottom:8px">Save these 12 recovery words:</p>
            <div class="recovery-grid">${grid}</div>
            <button class="btn btn-secondary btn-sm" id="copy-recovery"><i data-lucide="copy" style="width:14px;height:14px"></i> Copy</button>
            <div style="margin-top:16px">
              <p style="font-size:13px;color:var(--text-secondary);margin-bottom:8px">Confirm 3 words to save:</p>
              <form id="confirm-recovery-form">
                ${positions.map(p => `<div class="field"><label>Word #${p + 1}</label><input type="text" name="word${p}" required autocomplete="off" autocapitalize="none"></div>`).join('')}
                <button type="submit" class="btn btn-primary btn-sm" style="margin-top:8px">Save Recovery Words</button>
                <button type="button" class="btn btn-secondary btn-sm" style="margin-top:8px" id="recovery-start-over">Start Over — New Words</button>
              </form>
            </div>
          `;
          lucideIcons();

          document.getElementById('copy-recovery').onclick = () => {
            navigator.clipboard.writeText(mnemonic).then(() => {
              const btn = document.getElementById('copy-recovery');
              btn.textContent = 'Copied!';
              setTimeout(() => { navigator.clipboard.writeText(''); }, 60000);
              setTimeout(() => { btn.innerHTML = '<i data-lucide="copy" style="width:14px;height:14px"></i> Copy'; lucideIcons(); }, 2000);
            });
          };

          document.getElementById('recovery-start-over').onclick = () => {
            flowEl.style.display = 'none';
            regenBtn.disabled = false;
            regenBtn.click();
          };

          document.getElementById('confirm-recovery-form').onsubmit = async (ev) => {
            ev.preventDefault();
            recoveryMsg.innerHTML = '';
            const btn = ev.target.querySelector('button');

            for (const p of positions) {
              const input = ev.target[`word${p}`].value.trim().toLowerCase();
              if (input !== words[p]) {
                recoveryMsg.innerHTML = '<div class="msg msg-error">One or more words are incorrect.</div>';
                return;
              }
            }

            btn.disabled = true;
            btn.textContent = 'Saving...';

            const recovery = await deriveRecoveryKeys(mnemonic);
            try {
              const wrapped = wrapMasterKeyForRecovery(recovery.wrappingKey);
              const authHex = Array.from(recovery.authKey).map(b => b.toString(16).padStart(2, '0')).join('');

              await post('/auth/set-recovery-key', {
                recovery_wrapped_master_key: Array.from(wrapped),
                recovery_auth_key: authHex,
              });

              recoveryMsg.innerHTML = '<div class="msg msg-success">Recovery words saved successfully.</div>';
              flowEl.style.display = 'none';
              regenBtn.disabled = false;
              regenBtn.textContent = 'Regenerate Recovery Words';
            } catch (err) {
              recoveryMsg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
              btn.disabled = false;
              btn.textContent = 'Save Recovery Words';
            } finally {
              wipe(recovery.wrappingKey, recovery.authKey);
            }
          };
        } catch (err) {
          recoveryMsg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
          regenBtn.disabled = false;
        }
      };
    }

    // Key rotation handler
    const rotateBtn = document.getElementById('rotate-key-btn');
    if (rotateBtn) {
      rotateBtn.onclick = async () => {
        const rotateMsg = document.getElementById('rotate-msg');
        const passwordInput = document.getElementById('rotate-password');
        const recoveryInput = document.getElementById('rotate-recovery');
        const password = passwordInput.value;

        if (!password) {
          rotateMsg.innerHTML = '<div class="msg msg-error">Enter your current passphrase.</div>';
          return;
        }
        if (!await confirmDialog('This will generate a new master key and re-wrap all item keys. Continue?', { danger: true })) return;

        rotateBtn.disabled = true;
        rotateBtn.textContent = 'Rotating...';
        rotateMsg.innerHTML = '';

        try {
          const items = await get('/items');
          const rotated = rotateMasterKey(items, password, me.client_salt);

          // Re-wrap recovery if mnemonic provided
          const recoveryMnemonic = recoveryInput ? recoveryInput.value.trim() : '';
          if (recoveryMnemonic) {
            const recovery = await deriveRecoveryKeys(recoveryMnemonic);
            try {
              const recoveryWrapped = wrapKeyForRecovery(recovery.wrappingKey, rotated.newMk);
              rotated.recovery_wrapped_master_key = Array.from(recoveryWrapped);
            } finally {
              wipe(recovery.wrappingKey, recovery.authKey);
            }
          }

          // Re-encrypt V0 blobs to V1 with AAD + re-wrap file keys inside envelopes.
          // oldEncKey is a snapshot of the pre-rotation encKey held while re-wrapping
          // items; the finally below wipes it on every exit so it doesn't linger
          // on the heap whether rotation succeeds or aborts. (#136)
          const oldEncKey = getEncKey();
          try {
            const blobUpdates = [];
            for (let i = 0; i < items.length; i++) {
              const item = items[i];
              if (!item.encrypted_blob) continue;
              rotateBtn.textContent = `Upgrading blobs... ${i + 1}/${items.length}`;
              try {
                // First: re-wrap file keys inside envelope (old encKey → new encKey)
                let blob64 = item.encrypted_blob;
                let changed = false;
                try {
                  const result = rewrapEnvelopeFileKeys(item.wrapped_key, item.nonce, blob64, me.user_id, oldEncKey, rotated.newEncKey);
                  if (result.changed) { blob64 = result.blob; changed = true; }
                } catch (err) {
                  console.warn(`file key re-wrap failed for ${item.id}:`, err);
                }
                // Second: upgrade V0 envelope to V1 if needed
                const reEncrypted = reEncryptBlobV1(item.wrapped_key, item.nonce, blob64, me.user_id);
                if (reEncrypted) { blob64 = reEncrypted; changed = true; }
                if (!changed) continue;
                const blobBytes = Uint8Array.from(atob(blob64), c => c.charCodeAt(0));
                const urlResp = await post('/items/upload-url', { size_bytes: blobBytes.byteLength });
                await fetch(urlResp.upload_url, { method: 'PUT', body: blobBytes });
                blobUpdates.push({ item_id: item.id, s3_key: urlResp.s3_key, size_bytes: blobBytes.byteLength });
              } catch (err) {
                console.warn(`blob upgrade failed for ${item.id}:`, err);
              }
            }

            rotateBtn.textContent = 'Committing...';
            await post('/auth/rotate-master-key', {
              encrypted_master_key: rotated.encrypted_master_key,
              encrypted_private_key: rotated.encrypted_private_key,
              items: rotated.items,
              recovery_wrapped_master_key: rotated.recovery_wrapped_master_key,
              passkey_rewraps: rotated.passkey_rewraps,
              blob_updates: blobUpdates,
            });

            // Apply new keys to in-memory state only after server confirms
            applyRotatedKeys(rotated.newMk, rotated.newEncKey);
            passwordInput.value = '';
            if (recoveryInput) recoveryInput.value = '';

            const blobMsg = blobUpdates.length ? ` ${blobUpdates.length} blob(s) upgraded to V1.` : '';
            rotateMsg.innerHTML = `<div class="msg msg-success">Master key rotated. ${rotated.items.length} item(s) re-wrapped.${blobMsg}</div>`;
          } finally {
            wipe(oldEncKey);
          }
        } catch (err) {
          rotateMsg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
        }
        rotateBtn.disabled = false;
        rotateBtn.textContent = 'Rotate Master Key';
      };
    }

    // Delete passkey buttons
    container.querySelectorAll('.delete-pk-btn').forEach(btn => {
      btn.onclick = async () => {
        if (!await confirmDialog('Delete this passkey?', { danger: true })) return;
        try {
          await del(`/passkeys/${btn.dataset.id}`);
          renderSettings(container);
        } catch (err) {
          msg.el.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
        }
      };
    });

    // Add passkey button
    const addBtn = document.getElementById('add-passkey-btn');
    if (addBtn) {
      addBtn.onclick = async () => {
        const name = await promptDialog('Passkey name:', { placeholder: 'e.g. "MacBook Touch ID"' });
        if (name === null) return;

        msg.el.innerHTML = '<div class="msg">Registering passkey... follow the browser prompt.</div>';
        addBtn.disabled = true;

        try {
          const reg = await startRegistration(name);

          if (reg.prf_enabled) {
            msg.el.innerHTML = '<div class="msg">Activating passwordless unlock... follow the browser prompt again.</div>';
            const prfResult = await activatePrf(reg.credential_id, reg.prf_salt);

            if (prfResult.prf_output) {
              const wrapped = wrapMasterKeyWithPrf(prfResult.prf_output);
              await finishActivatePrf(prfResult.state_id, prfResult.assertion, wrapped);
              msg.el.innerHTML = '<div class="msg msg-success">Passkey enrolled with passwordless unlock!</div>';
            } else {
              msg.el.innerHTML = '<div class="msg msg-success">Passkey enrolled (PRF output unavailable).</div>';
            }
          } else {
            msg.el.innerHTML = '<div class="msg msg-success">Passkey enrolled (authentication only).</div>';
          }
          renderSettings(container);
        } catch (err) {
          if (err.name === 'NotAllowedError') {
            msg.el.innerHTML = '<div class="msg msg-error">Passkey operation was cancelled.</div>';
          } else {
            msg.el.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
          }
          addBtn.disabled = false;
        }
      };
    }
  } catch (err) {
    container.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
  }
}

async function formatKeyFingerprint(pubKey) {
  if (!pubKey || pubKey.length <= 1) return 'Not set';
  const hash = await crypto.subtle.digest('SHA-256', new Uint8Array(pubKey));
  const hex = Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
  return hex.slice(0, 8) + '...' + hex.slice(-8);
}
